import React from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { LoadingSpinner } from '../../LoadingSpinner';
import { DatePicker } from '../../DatePicker';
import {
  EmployeeOverviewMultiSelect,
  type EmployeeOverviewFilterOption,
} from './EmployeeOverviewMultiSelect';
import { formatPeriodLabel } from '../../../utils/employeeRoutes';
import type { PeriodGranularity } from './useEmployeePeriodQuery';

type EmployeeOverviewPeriodToolbarProps = {
  from: string;
  to: string;
  granularity: PeriodGranularity;
  onGranularityChange: (g: 'month' | 'week') => void;
  onShift: (direction: -1 | 1) => void;
  onApplyCustomPeriod: (range: { from: string; to: string }) => void;
  filterEmployees: EmployeeOverviewFilterOption[];
  selectedEmployeeIds: string[];
  onSelectedEmployeeIdsChange: (ids: string[]) => void;
  onOpenExport?: () => void;
  exportBusy?: boolean;
};

export function EmployeeOverviewPeriodToolbar({
  from,
  to,
  granularity,
  onGranularityChange,
  onShift,
  onApplyCustomPeriod,
  filterEmployees,
  selectedEmployeeIds,
  onSelectedEmployeeIdsChange,
  onOpenExport,
  exportBusy = false,
}: EmployeeOverviewPeriodToolbarProps) {
  const [rangeOpen, setRangeOpen] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState(from);
  const [draftTo, setDraftTo] = React.useState(to);
  const [rangeError, setRangeError] = React.useState<string | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!rangeOpen) return;
    setDraftFrom(from);
    setDraftTo(to);
    setRangeError(null);
  }, [rangeOpen, from, to]);

  React.useEffect(() => {
    if (!rangeOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setRangeOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [rangeOpen]);

  const applyRange = () => {
    if (!draftFrom || !draftTo) {
      setRangeError('Bitte Start- und Enddatum wählen.');
      return;
    }
    if (draftFrom > draftTo) {
      setRangeError('Das Startdatum muss vor dem Enddatum liegen.');
      return;
    }
    onApplyCustomPeriod({ from: draftFrom, to: draftTo });
    setRangeOpen(false);
    setRangeError(null);
  };

  const rangePanel =
    rangeOpen && triggerRef.current ? (
      <div
        ref={panelRef}
        className="fixed z-[200] rounded-lg border border-app-border bg-app-surface p-4 shadow-lg"
        style={{
          top: triggerRef.current.getBoundingClientRect().bottom + 6,
          left: triggerRef.current.getBoundingClientRect().left,
          width: 320,
          maxWidth: 'min(360px, calc(100vw - 24px))',
        }}
        role="dialog"
        aria-label="Zeitraum wählen"
      >
        <p className="text-xs font-semibold text-app-text mb-3">Zeitraum</p>
        <div className="grid grid-cols-1 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-app-text-muted">Von</span>
            <DatePicker value={draftFrom} onChange={setDraftFrom} className="mt-1 w-full" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-app-text-muted">Bis</span>
            <DatePicker value={draftTo} onChange={setDraftTo} className="mt-1 w-full" />
          </label>
        </div>
        {rangeError ? <p className="mt-2 text-xs text-red-600">{rangeError}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setRangeOpen(false)}
            className="rounded-md border border-app-border px-3 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={applyRange}
            className="rounded-md bg-pipedrive-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-95"
          >
            Übernehmen
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="shrink-0 flex flex-wrap items-center gap-3 px-6 py-3 bg-app-surface border-b border-app-border">
      <div className="flex items-center gap-1 p-0.5 bg-app-surface-muted rounded-lg">
        {(
          [
            ['month', 'Monat'],
            ['week', 'Woche'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => onGranularityChange(id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              granularity === id ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-subtle hover:text-app-text-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="inline-flex items-center rounded-md border border-app-border bg-app-surface overflow-hidden">
        <button
          type="button"
          onClick={() => onShift(-1)}
          className="w-8 h-8 inline-flex items-center justify-center text-app-text-muted hover:bg-app-hover border-r border-app-border"
          aria-label="Vorheriger Zeitraum"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setRangeOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-app-text hover:bg-app-hover min-w-[180px] justify-center"
          aria-expanded={rangeOpen}
          aria-haspopup="dialog"
        >
          <Calendar className="w-3.5 h-3.5 text-app-text-subtle shrink-0" />
          <span>{formatPeriodLabel(from, to)}</span>
        </button>
        <button
          type="button"
          onClick={() => onShift(1)}
          className="w-8 h-8 inline-flex items-center justify-center text-app-text-muted hover:bg-app-hover border-l border-app-border"
          aria-label="Nächster Zeitraum"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 min-w-[8px]" />

      <EmployeeOverviewMultiSelect
        employees={filterEmployees}
        selected={selectedEmployeeIds}
        onChange={onSelectedEmployeeIdsChange}
      />

      {onOpenExport ? (
        <button
          type="button"
          onClick={onOpenExport}
          disabled={exportBusy || filterEmployees.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-app-border-strong bg-app-surface px-3 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
        >
          {exportBusy ? (
            <LoadingSpinner size="sm" className="text-app-text-subtle" />
          ) : (
            <Download className="h-3.5 w-3.5 text-app-text-subtle" />
          )}
          Stammdaten Exportieren
        </button>
      ) : null}

      {rangeOpen && typeof document !== 'undefined' ? createPortal(rangePanel, document.body) : null}
    </div>
  );
}
